import {
  GENERIC_ERROR_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
  resolveBusinessErrorMessage,
} from '@core/errors/business-error-messages';
import { normalizeErrorCode } from '@core/models/error.model';

// Re-exported so the many existing importers of these constants keep working.
// Their definitions live with the error-code registry, which is the single place
// that owns user-facing error copy.
export { GENERIC_ERROR_MESSAGE, SESSION_EXPIRED_MESSAGE };

/**
 * A structured, transport-agnostic representation of a failed API call.
 *
 * The API layer (`SendApi`/`WithdrawalApi`) catches the raw `HttpErrorResponse`
 * and rethrows it as a `GozemApiError` so that downstream stores keep the data
 * they need WITHOUT depending on Angular's `HttpErrorResponse` shape leaking
 * through every layer:
 *  - `displayMessage` — safe, prefix-stripped, user-facing text (verbatim from
 *    the server when available; otherwise the caller's fallback).
 *  - `code` — the backend business code (e.g. "1052", "1061").
 *  - `remainingAttempts` — PIN attempts left before lockout; `null` when the
 *    error is not PIN-related. `0` means the account is now locked.
 *  - `httpStatus` — the HTTP status, for logging / edge cases.
 *
 * This is the single discriminator the PIN flows rely on: a PIN/lockout error
 * is exactly one where `remainingAttempts !== null`.
 */
export class GozemApiError extends Error {
  constructor(
    readonly displayMessage: string,
    readonly code: string | null,
    readonly remainingAttempts: number | null,
    readonly httpStatus: number | null,
  ) {
    super(displayMessage);
    this.name = 'GozemApiError';
    // Restore the prototype chain for `instanceof` after transpilation to ES5+.
    Object.setPrototypeOf(this, GozemApiError.prototype);
  }
}

/**
 * Build a {@link GozemApiError} from any caught value (typically an
 * `HttpErrorResponse`). Extracts the business code and `remaining_attempts`
 * metadata, and derives the safe display text via {@link extractErrorMessage}.
 */
export function toGozemApiError(error: unknown, fallback: string): GozemApiError {
  let code: string | null = null;
  let remainingAttempts: number | null = null;
  let httpStatus: number | null = null;

  if (error && typeof error === 'object') {
    const err = error as {
      status?: unknown;
      error?: { error_code?: unknown; metadata?: { remaining_attempts?: unknown } };
    };
    httpStatus = typeof err.status === 'number' ? err.status : null;
    // normalizeErrorCode, not a `typeof === 'string'` check: these APIs send the
    // code as a JSON number on some endpoints, and dropping it there silently
    // defeated every downstream `switch (err.code)`.
    code = normalizeErrorCode(err.error?.error_code);
    const remaining = err.error?.metadata?.remaining_attempts;
    remainingAttempts = typeof remaining === 'number' ? remaining : null;
  }

  // Our own French copy wins over the server's English text for codes we know.
  // For everything else the server message (or the caller's fallback) stands.
  const displayMessage = resolveBusinessErrorMessage(code, extractErrorMessage(error, fallback));

  return new GozemApiError(displayMessage, code, remainingAttempts, httpStatus);
}

/**
 * True when an error is an authentication-stage rejection tied to the PIN input
 * itself (wrong PIN, lockout, or a malformed-PIN rejection) rather than a
 * transaction failure that happened after the PIN was accepted.
 *
 * Classification order, most to least reliable:
 *  1. `remainingAttempts !== null` — the backend explicitly tagged a PIN attempt.
 *  2. Heuristic fallback: the message clearly names the PIN together with a
 *     format/validity problem. Deliberately narrow (both patterns must match) to
 *     avoid misrouting a genuine transfer failure onto the PIN step.
 *
 * TODO(backend): if `init_psp_transfer` starts returning a stable `error_code`
 * for PIN-format rejections, match on `err.code` here and drop the heuristic —
 * a code is a far more robust discriminator than message text.
 */
export function isAuthStageError(err: unknown): boolean {
  if (!(err instanceof GozemApiError)) return false;
  if (err.remainingAttempts !== null) return true;
  const message = err.displayMessage;
  return (
    /\bpin\b/i.test(message) && /(format|invalid|invalide|incorrect|chiffre|digit)/i.test(message)
  );
}

/**
 * Turns any caught error into safe, user-friendly text for display in a toast or
 * inline message.
 *
 * The key guarantee: a raw `HttpErrorResponse.message` is NEVER surfaced. Angular's
 * `HttpClient` builds that message as `"Http failure response for <url>: <status> <text>"`,
 * which leaks the internal API URL and HTTP status to the user. We instead prefer a
 * structured server-provided message (`error.error.message`) and otherwise fall back to
 * the caller's friendly default.
 *
 * The 5xx rule is about the *body*, not the status: a body carrying an `error_code` is a
 * deliberate business error and is safe to surface whatever the status; an uncoded 5xx is
 * an unhandled crash whose body can hold stack traces, so it always falls back. Gating on
 * status alone made every business error the backend chose to send as a 500 render as the
 * generic message — that is how code "1032" reached staging as "Une erreur est survenue".
 *
 * @param error    The value caught in a `catchError` / `catch` block (unknown shape).
 * @param fallback A user-friendly message to show when no safe server message is available.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as {
    status?: unknown;
    error?: { message?: unknown; error_message?: unknown; error_code?: unknown };
    message?: unknown;
  };

  // 1. Prefer a structured server message: `error.error.error_message`, then `error.error.message`.
  //    Some endpoints return the business text under `error_message` (e.g. the wallet init
  //    endpoints), others under `message`.
  //    The backend prepends a machine code on some endpoints, e.g. "1052 - <message>"
  //    or "NO_DATA_FOUND - <message>"; strip it so only the human text remains.
  //    Skip this only for an UNCODED 5xx: those bodies can carry technical text
  //    (stack traces, internal messages) that we must not surface — fall back instead.
  //    A 5xx that carries an `error_code` is a deliberate business error, so its
  //    message is trustworthy and gets used.
  const code = normalizeErrorCode(err.error?.error_code);
  const isUncodedServerError = typeof err.status === 'number' && err.status >= 500 && code === null;
  const serverMessage =
    (typeof err.error?.error_message === 'string' ? err.error.error_message : undefined) ??
    err.error?.message;
  if (!isUncodedServerError && typeof serverMessage === 'string' && serverMessage.trim()) {
    const cleaned = stripServerErrorPrefix(serverMessage, code ?? undefined);
    if (cleaned) {
      return cleaned;
    }
  }

  // 2. Never expose the raw HttpClient message ("Http failure response for <url>: <status>").
  //    Any HttpErrorResponse / network failure / unknown shape resolves to the fallback.
  return fallback;
}

/**
 * Strips the leading machine error-code prefix the API prepends to user-facing
 * messages, e.g. "NO_DATA_FOUND - Aucun paiement…" or "1052 - Code erroné…".
 * Removes the exact `code` when present, plus a bare numeric prefix as a fallback.
 */
export function stripServerErrorPrefix(message: string, code?: string): string {
  let result = message;
  if (code) {
    const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(`^${escaped}\\s*-\\s*`), '');
  }
  result = result.replace(/^\d+\s*-\s*/, ''); // bare numeric prefix fallback
  return result.trim();
}
